import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Award, BookOpen, Users, Send, Check, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

const EmitirCertificados = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [modelos, setModelos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [selectedModelo, setSelectedModelo] = useState(null);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [selectedParticipantes, setSelectedParticipantes] = useState([]);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    loadModelos();
    loadCursos();
  }, []);

  const loadModelos = async () => {
    try {
      const res = await api.get('/cert-modelos', { params: { limit: 100, ativo: 1 } });
      setModelos(res.data.data || []);
    } catch (err) { toast.error('Erro ao carregar modelos'); }
  };

  const loadCursos = async () => {
    try {
      const res = await api.get('/cursos', { params: { limit: 100 } });
      setCursos(res.data.data || []);
    } catch (err) { toast.error('Erro ao carregar cursos'); }
  };

  const loadParticipantesCurso = async (cursoId) => {
    try {
      const res = await api.get(`/certificados/curso/${cursoId}/participantes`);
      setParticipantes(res.data.data || []);
    } catch (err) { toast.error('Erro ao carregar participantes'); }
  };

  const handleCursoChange = (cursoId) => {
    const curso = cursos.find(c => String(c.id_curso) === String(cursoId));
    setSelectedCurso(curso || null);
    setSelectedParticipantes([]);
  };

  const toggleParticipante = (id) => {
    setSelectedParticipantes(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selecionarTodos = () => {
    if (selectedParticipantes.length === participantes.length) {
      setSelectedParticipantes([]);
    } else {
      setSelectedParticipantes(participantes.map(p => p.id_participa));
    }
  };

  const gerarPDF = async () => {
    if (selectedParticipantes.length === 0) {
      toast.error('Selecione pelo menos um participante');
      return;
    }
    setGerando(true);
    try {
      const response = await api.post('/certificados/gerar-pdf', {
        id_modelo: selectedModelo.id_modelo,
        id_curso: selectedCurso.id_curso,
        participantes: selectedParticipantes
      }, { responseType: 'blob' });

      // Criar blob e fazer download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificados_${selectedCurso.id_curso}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${selectedParticipantes.length} certificado(s) gerado(s) com sucesso!`);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      toast.error('Erro ao gerar certificados');
    } finally {
      setGerando(false);
    }
  };

  const passos = [
    { num: 1, label: 'Modelo', icon: Award },
    { num: 2, label: 'Curso', icon: BookOpen },
    { num: 3, label: 'Participantes', icon: Users },
    { num: 4, label: 'Conferência', icon: Check }
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <IconButton icon={ArrowLeft} onClick={() => navigate('/cert-modelos')} variant="secondary" size="sm" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Emitir Certificados</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Selecione modelo, curso e participantes</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-center mb-8 gap-2">
        {passos.map((p, i) => {
          const Icon = p.icon;
          const active = step === p.num;
          const done = step > p.num;
          return (
            <React.Fragment key={p.num}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                active ? 'bg-blue-600 text-white' : done ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{p.label}</span>
              </div>
              {i < passos.length - 1 && <ChevronRight className="w-4 h-4 text-gray-400" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Modelo */}
      {step === 1 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Selecione o modelo</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modelos.map(m => (
              <button key={m.id_modelo} onClick={() => setSelectedModelo(m)}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  selectedModelo?.id_modelo === m.id_modelo
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{m.nome_modelo}</p>
                    <p className="text-sm text-gray-500">{m.desc_modelo || 'Sem descrição'}</p>
                    <p className="text-xs text-gray-400 mt-1">{m.largura_m} × {m.altura_m} mm · {m.orientacao}</p>
                  </div>
                  {selectedModelo?.id_modelo === m.id_modelo && <Check className="w-5 h-5 text-blue-600" />}
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <button disabled={!selectedModelo} onClick={() => setStep(2)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              Próximo
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Curso */}
      {step === 2 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Selecione o curso</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cursos.map(c => (
              <button key={c.id_curso} onClick={() => handleCursoChange(c.id_curso)}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  selectedCurso?.id_curso === c.id_curso
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{c.descr_curso}</p>
                    <p className="text-sm text-gray-500">Facilitador: {c.nome_facilitador}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {c.dt_inicio} até {c.dt_fim}
                    </p>
                  </div>
                  {selectedCurso?.id_curso === c.id_curso && <Check className="w-5 h-5 text-blue-600" />}
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(1)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
              Voltar
            </button>
            <button disabled={!selectedCurso} onClick={() => { loadParticipantesCurso(selectedCurso.id_curso); setStep(3); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              Próximo
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Participantes */}
      {step === 3 && selectedCurso && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Participantes de {selectedCurso.descr_curso}
            </h2>
            <span className="text-sm text-gray-500">{participantes.length} participante(s)</span>
          </div>

          {participantes.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Nenhum participante cadastrado neste curso. Cadastre ou importe participantes informando o curso.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selectedParticipantes.length === participantes.length && participantes.length > 0}
                    onChange={selecionarTodos} className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Selecionar todos</span>
                </label>
                <span className="text-sm text-gray-500">{selectedParticipantes.length} selecionado(s)</span>
              </div>
              <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full">
                  <tbody>
                    {participantes.map(p => (
                      <tr key={p.id_participa} className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-3 py-2 w-10">
                          <input type="checkbox" checked={selectedParticipantes.includes(p.id_participa)}
                            onChange={() => toggleParticipante(p.id_participa)} className="w-4 h-4 rounded" />
                        </td>
                        <td className="px-3 py-2 text-sm dark:text-white">{p.nome_participa}</td>
                        <td className="px-3 py-2 text-sm text-gray-500">{p.email_participa || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(2)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
              Voltar
            </button>
            <button disabled={selectedParticipantes.length === 0} onClick={() => setStep(4)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
              Próximo
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Conferência */}
      {step === 4 && selectedModelo && selectedCurso && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Conferência</h2>
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Modelo</span>
              <span className="font-medium dark:text-white">{selectedModelo.nome_modelo}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Curso</span>
              <span className="font-medium dark:text-white">{selectedCurso.descr_curso}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">Participantes</span>
              <span className="font-medium dark:text-white">{selectedParticipantes.length}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <span className="text-sm text-green-700 dark:text-green-400">Páginas a gerar (2 por participante)</span>
              <span className="font-bold text-green-700 dark:text-green-400">{selectedParticipantes.length * 2}</span>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
              Voltar
            </button>
            <div className="flex items-center gap-3">
              <button onClick={() => setStep(1)} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
                Inicial
              </button>
              <button onClick={gerarPDF} disabled={gerando}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50">
                <Send className="w-4 h-4" />
                {gerando ? 'Gerando...' : 'Gerar Certificados PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmitirCertificados;
